import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { CtaButton } from '../CtaButton/CtaButton';
import type { CtaButtonColorVariant } from '../CtaButton/CtaButton';
import { RichText } from '../RichText/RichText';
import { Badge } from '../Badge/Badge';
import { CustomIcon } from '../CustomIcon/CustomIcon';
import type { CustomIconName } from '../CustomIcon/CustomIcon';
import { blockAnchorAttributes } from '../../utils/blockAnchor';
import { hasCtaDestination } from '../../utils/ctaAction';
import type { CtaAction } from '../../utils/ctaAction';
import './LeadCapture.scss';

/**
 * LeadCapture — Email + optional name/website/consent capture block for marketing pages.
 *
 * Two layout variants:
 * - `card` (default): Rounded, bordered card with drop shadow. Standalone use.
 * - `inline`: Flat, no chrome. For embedding in Hero, SplitContent, or PreFooter.
 *
 * Field configurations (via `fields` prop):
 * - `email-only`: Just email input.
 * - `email-name`: Name (optional) + email inputs.
 * - `email-website`: Business website (optional) + email inputs.
 * - `email-consent`: Email input + consent checkbox.
 * - `email-name-consent`: Name + email + consent checkbox.
 *
 * State machine: idle → submitting → success | error. Success/error replace the
 * form inline. Submission targets `submitEndpoint` when provided (encoding
 * controlled by `submitFormat`: `form` = application/x-www-form-urlencoded
 * for Pardot / legacy CRMs (default), `json` = application/json for custom
 * REST endpoints);
 * without it, the form fires `onSubmit` (Storybook / tests). With neither, the
 * submission has nowhere to go, so the form enters the error state rather than
 * reporting a success it cannot deliver. That configuration mistake marks the
 * error state with `data-poly-error`, like other misconfigured blocks; a
 * delivery failure (HTTP status, network error, rejected `onSubmit`) is
 * recorded in `data-poly-submit-failure` instead, so outages are not reported
 * as authoring errors. Neither attribute contains the visitor's data.
 *
 * Layout: heading + subhead in the left column, form in the right column at
 * ≥768px (64px column gap). Stacks to a vertical column at ≤767px.
 */

const NO_DELIVERY_PATH = 'no submitEndpoint or onSubmit';

export const LEAD_CAPTURE_VARIANTS = ['card', 'inline'] as const;
export type LeadCaptureVariant = (typeof LEAD_CAPTURE_VARIANTS)[number];

export const LEAD_CAPTURE_FIELDS = [
  'email-only',
  'email-name',
  'email-website',
  'email-consent',
  'email-name-consent',
] as const;
export type LeadCaptureFieldsConfig = (typeof LEAD_CAPTURE_FIELDS)[number];

/** Payload emitted by onSubmit and posted to submitEndpoint. */
export interface LeadCaptureSubmission {
  email: string;
  name?: string;
  website?: string;
  consent?: boolean;
}

/**
 * @polySemanticUsage About this block: a form-based block designed to collect user information
 * (e.g. email, name, business details). Use it for gated content, newsletter sign-ups, contact
 * requests, or registration starts. May include supporting copy, trust signals, and a submit CTA.
 */
export interface LeadCaptureProps {
  /**
   * Layout variant.
   * - `card`: Bordered, rounded, shadowed. Use standalone.
   * - `inline`: Flat, for embedding in Hero/SplitContent/PreFooter.
   * @polyType SelectList
   * @polyLabel Layout Variant
   * @polyOptions Card=card,Inline=inline
   * @polyDefault card
   */
  variant?: LeadCaptureVariant;

  /**
   * Which fields render in the form.
   * @polyType SelectList
   * @polyLabel Form Fields
   * @polyOptions Email only=email-only,Email + Name=email-name,Email + Website=email-website,Email + Consent=email-consent,Email + Name + Consent=email-name-consent
   * @polyDefault email-only
   */
  fields?: LeadCaptureFieldsConfig;

  /**
   * Main heading (H2). Required.
   * @polyLabel Heading
   */
  heading: string;

  /** Optional dark pill badge above the heading. */
  badgeText?: string;

  /**
   * Built-in icon rendered inside the badge (e.g. 'bolt').
   * @polyType SelectList
   * @polyLabel Badge Icon
   * @polyOptions Cart=cart,Storefront=storefront,Package=package,Analytics=analytics,Star=star,Check=check,Arrow Forward=arrow-forward,Lock=lock,Menu=menu,Play=play,Search=search,Close=close,Globe=globe,Language=language,Arrow Left=arrow-left,Chevron Down=chevron-down,Chevron Right=chevron-right
   */
  badgeIcon?: CustomIconName;

  /**
   * Optional authored icon URL rendered inside the badge.
   * @polyType Url
   * @polyLabel Badge Icon URL
   */
  badgeIconUrl?: string;

  /** Optional eyebrow text above the heading (use with or instead of a badge). */
  eyebrow?: string;

  /**
   * Optional subheading (Markdown supported).
   * @polyType RichText
   * @polyLabel Subheading
   */
  subheading?: string;

  /**
   * Style for the optional list rendered between the subheading and the form.
   * Pick one — numbered (ordered) or bulleted (unordered).
   * @polyType SelectList
   * @polyLabel List Style
   * @polyOptions Numbered=numbered,Bulleted=bulleted
   * @polyDefault numbered
   * @polyTranslatable false
   */
  listStyle?: 'numbered' | 'bulleted';

  /** List item 1 (Markdown inline: `**bold**`, `[links](url)`). */
  listItem1?: string;
  /** List item 2. */
  listItem2?: string;
  /** List item 3. */
  listItem3?: string;
  /** List item 4 (maximum 4 items). */
  listItem4?: string;

  /**
   * Label for the email input. Also used as aria-label when a visible label
   * is not shown. Defaults to "Email address".
   * @polyLabel Email Field Label
   */
  emailLabel?: string;

  /** Placeholder text for the email input. */
  emailPlaceholder?: string;

  /**
   * Label for the name input (when the field is enabled).
   * Defaults to "Full name".
   * @polyLabel Name Field Label
   */
  nameLabel?: string;

  /** Placeholder text for the name input. */
  namePlaceholder?: string;

  /**
   * Label for the business website input (when the field is enabled).
   * Defaults to "Business website". The value is sent as `website`.
   * @polyLabel Website Field Label
   */
  websiteLabel?: string;

  /** Placeholder text for the business website input. */
  websitePlaceholder?: string;

  /**
   * Rich text label for the consent checkbox (when the field is enabled).
   * Supports Markdown so authors can link to policies inline.
   * @polyType RichText
   * @polyLabel Consent Text
   */
  consentText?: string;

  /**
   * Plain-text accessible name for the consent checkbox. Set this when
   * `consentText` contains links or other rich formatting: screen readers use
   * it as the checkbox's label instead of flattening the rich content (which
   * would fold link text into the control's name). When omitted, the checkbox
   * falls back to being labelled by the rendered consent text.
   * @polyLabel Consent Accessible Label
   */
  consentLabel?: string;

  /**
   * Text for the submit button. Defaults to "Submit".
   * @polyLabel Submit Button Text
   */
  submitCtaText?: string;

  /**
   * Color variant for the submit button. Defaults to `primary`.
   * @polyType SelectList
   * @polyLabel Submit Button Style
   * @polyOptions Primary=primary,Secondary=secondary,Contrast=contrast,Tertiary=tertiary,Off-White=offWhite
   * @polyDefault primary
   */
  submitCtaColorVariant?: CtaButtonColorVariant;

  /**
   * Optional short line of supporting/incentive copy rendered below the
   * submit button (Markdown supported). Use for things like
   * "It's free to sign up · No credit card required".
   * @polyType RichText
   * @polyLabel Incentives Text
   * @polyTranslatable true
   */
  incentivesText?: string;

  /**
   * Endpoint the form POSTs to on submit. Wire format is controlled by
   * `submitFormat` (default `form` — Pardot / legacy CRM compatible).
   * When omitted, the form calls `onSubmit` without a network request; if
   * `onSubmit` is also absent, submitting shows the error state because the
   * lead cannot be delivered anywhere.
   * @polyType Url
   * @polyLabel Submit Endpoint
   */
  submitEndpoint?: string;

  /**
   * Content-type used when POSTing to `submitEndpoint`. Use `form`
   * (application/x-www-form-urlencoded) for Pardot Form Handlers,
   * Salesforce Web-to-Lead, and most legacy CRM endpoints. Use `json`
   * (application/json) for custom REST endpoints (e.g. an internal Lambda)
   * that expect a JSON body. Defaults to `form`.
   * @polyType SelectList
   * @polyLabel Submit Format
   * @polyOptions Form (Pardot / legacy CRM)=form,JSON (custom REST API)=json
   * @polyDefault form
   */
  submitFormat?: 'form' | 'json';

  /**
   * Optional callback invoked with the payload before submission. Return a
   * Promise that rejects to force the error state; resolve (or return void)
   * for the success path. Programmatic only.
   * @polyIgnore
   */
  onSubmit?: (payload: LeadCaptureSubmission) => void | Promise<void>;

  /**
   * Heading rendered after a successful submission — the single line shown
   * inside the success notification pill. Defaults to "Thanks for signing up!"
   * @polyLabel Success Heading
   */
  successHeading?: string;

  /**
   * Optional CTA button on the right of the success state. Renders only when
   * both `successCtaText` and `successCtaHref` are set.
   * @polyLabel Success CTA Text
   * @polyTranslatable true
   */
  successCtaText?: string;

  /**
   * Href for the optional success-state CTA button.
   * @polyType Url
   * @polyLabel Success CTA Link
   */
  successCtaHref?: string;

  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the CTA Link field.
   * @polyDefault link
   * @polyTranslatable false
   */
  successCtaAction?: CtaAction;

  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  successCtaTargetPopupId?: string;

  /**
   * Storybook / testing only. Renders the component directly in the given
   * state on mount, bypassing the form flow. DO NOT use in production
   * content — the state machine drives itself from user interaction.
   * @polyIgnore
   */
  defaultFormState?: FormState;

  /**
   * Heading rendered when the submission fails — the single line shown inside
   * the error notification pill. Defaults to "Something went wrong".
   * @polyLabel Error Heading
   */
  errorHeading?: string;

  /**
   * Optional CTA button on the right of the error state. Renders only when
   * both `errorCtaText` and `errorCtaHref` are set.
   * @polyLabel Error CTA Text
   * @polyTranslatable true
   */
  errorCtaText?: string;

  /**
   * Href for the optional error-state CTA button.
   * @polyType Url
   * @polyLabel Error CTA Link
   */
  errorCtaHref?: string;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the CTA Link field.
   * @polyDefault link
   * @polyTranslatable false
   */
  errorCtaAction?: CtaAction;

  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  errorCtaTargetPopupId?: string;
  /**
   * Anchor ID for in-page links: a link to `#<anchorId>` on the same page scrolls to this block.
   * Normalised to letters, numbers and dashes (spaces become dashes, a leading `#` and other
   * characters are dropped). Must be unique on the page. Migration: when any link on the source page points at `#<id>` and that id sits on the
   * source section this block replaces, copy the id here unchanged (without the `#`).
   * @polyType Text
   * @polyLabel Anchor ID
   * @polyHelp Lets in-page links such as #your-id jump to this block. Use letters, numbers and dashes (other characters are removed); must be unique on the page.
   * @polyTranslatable false
   */
  anchorId?: string;
}

type FormState = 'idle' | 'submitting' | 'success' | 'error';

const DEFAULT_EMAIL_LABEL = 'Email address';
const DEFAULT_NAME_LABEL = 'Full name';
const DEFAULT_WEBSITE_LABEL = 'Business website';
const DEFAULT_SUBMIT_TEXT = 'Submit';
const DEFAULT_SUCCESS_HEADING = 'Thanks for signing up!';
const DEFAULT_ERROR_HEADING = 'Something went wrong';

/** Lightweight client-side shape check to catch obvious typos before submit — not authoritative validation (the destination endpoint owns that). */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const includesName = (fields: LeadCaptureFieldsConfig): boolean =>
  fields === 'email-name' || fields === 'email-name-consent';

const includesWebsite = (fields: LeadCaptureFieldsConfig): boolean => fields === 'email-website';

const includesConsent = (fields: LeadCaptureFieldsConfig): boolean =>
  fields === 'email-consent' || fields === 'email-name-consent';

export const LeadCapture: React.FC<LeadCaptureProps> = ({
  variant = 'card',
  fields = 'email-only',
  heading,
  badgeText,
  badgeIcon,
  badgeIconUrl,
  eyebrow,
  subheading,
  listStyle = 'numbered',
  listItem1,
  listItem2,
  listItem3,
  listItem4,
  emailLabel = DEFAULT_EMAIL_LABEL,
  emailPlaceholder,
  nameLabel = DEFAULT_NAME_LABEL,
  namePlaceholder,
  websiteLabel = DEFAULT_WEBSITE_LABEL,
  websitePlaceholder,
  consentText,
  consentLabel,
  submitCtaText = DEFAULT_SUBMIT_TEXT,
  submitCtaColorVariant = 'primary',
  incentivesText,
  submitEndpoint,
  submitFormat = 'form',
  onSubmit,
  successHeading = DEFAULT_SUCCESS_HEADING,
  successCtaText,
  successCtaHref,
  successCtaAction,
  successCtaTargetPopupId,
  defaultFormState,
  errorHeading = DEFAULT_ERROR_HEADING,
  errorCtaText,
  errorCtaHref,
  errorCtaAction,
  errorCtaTargetPopupId,
  anchorId,
}) => {
  const fieldId = useId();
  const emailId = `${fieldId}-email`;
  const nameId = `${fieldId}-name`;
  const websiteId = `${fieldId}-website`;
  const consentId = `${fieldId}-consent`;
  const consentTextId = `${fieldId}-consent-text`;
  const emailErrorId = `${fieldId}-email-error`;

  const [name, setName] = useState('');
  const [website, setWebsite] = useState('');
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [formState, setFormState] = useState<FormState>(defaultFormState ?? 'idle');
  const [submitFailure, setSubmitFailure] = useState<string | null>(null);

  // Auto-dismiss the success confirmation after ~3.5s, returning to the form.
  // Skipped when defaultFormState is set (Storybook / testing preview).
  useEffect(() => {
    if (formState !== 'success' || defaultFormState) return undefined;
    const timer = setTimeout(() => setFormState('idle'), 3500);
    return () => clearTimeout(timer);
  }, [formState, defaultFormState]);

  const showName = useMemo(() => includesName(fields), [fields]);
  const showWebsite = useMemo(() => includesWebsite(fields), [fields]);
  const showConsent = useMemo(() => includesConsent(fields), [fields]);

  // Optional list (max 4 slots) rendered between the subheading and the form.
  // Reuses RichText's <ol>/<ul>: numbered -> "1. …", bulleted -> "- …".
  const listMarkdown = useMemo(() => {
    const items = [listItem1, listItem2, listItem3, listItem4].filter(
      (item): item is string => Boolean(item && item.trim()),
    );
    if (items.length === 0) return '';
    return items
      .map((item, i) => (listStyle === 'numbered' ? `${i + 1}. ${item}` : `- ${item}`))
      .join('\n');
  }, [listStyle, listItem1, listItem2, listItem3, listItem4]);

  // Dev-only guard: a consent checkbox needs an accessible name from either
  // consentLabel or consentText; with neither it has no label. WCAG 1.3.1/4.1.2.
  if (
    process.env.NODE_ENV !== 'production' &&
    showConsent &&
    (!consentLabel || !consentLabel.trim()) &&
    (!consentText || !consentText.trim())
  ) {
    // eslint-disable-next-line no-console
    console.warn(
      '[LeadCapture] fields includes consent but neither consentLabel nor ' +
        'consentText is set. The checkbox will have no accessible label ' +
        '(WCAG 1.3.1/4.1.2). Provide consentLabel (plain text) or consentText ' +
        '(Markdown), or switch fields to a variant without consent.'
    );
  }

  const validateEmail = useCallback((value: string): string | null => {
    if (!value.trim()) {
      return 'Please enter your email address.';
    }
    if (!EMAIL_REGEX.test(value.trim())) {
      return 'Please enter a valid email address.';
    }
    return null;
  }, []);

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const err = validateEmail(email);
      if (err) {
        setEmailError(err);
        return;
      }
      setEmailError(null);
      setFormState('submitting');

      const payload: LeadCaptureSubmission = {
        email: email.trim(),
        ...(showName && name.trim() ? { name: name.trim() } : {}),
        ...(showWebsite && website.trim() ? { website: website.trim() } : {}),
        ...(showConsent ? { consent } : {}),
      };

      let failureDetail = 'onSubmit rejected';
      try {
        if (onSubmit) {
          await onSubmit(payload);
        }
        if (submitEndpoint) {
          const isForm = submitFormat === 'form';
          // Pardot Form Handlers / Salesforce Web-to-Lead / most legacy CRMs
          // expect application/x-www-form-urlencoded; custom REST endpoints
          // typically want application/json. `submitFormat` (author-set)
          // controls which we send. `consent` is coerced to "true"/"false"
          // so it round-trips through form-urlencoded correctly.
          failureDetail = 'network error';
          const response = await fetch(submitEndpoint, {
            method: 'POST',
            headers: {
              'Content-Type': isForm
                ? 'application/x-www-form-urlencoded'
                : 'application/json',
            },
            body: isForm
              ? new URLSearchParams(
                  Object.entries(payload).reduce<Record<string, string>>(
                    (acc, [key, value]) => {
                      acc[key] = String(value);
                      return acc;
                    },
                    {}
                  )
                ).toString()
              : JSON.stringify(payload),
          });
          if (!response.ok) {
            failureDetail = `HTTP ${response.status}`;
            throw new Error(`Submit failed: HTTP ${response.status}`);
          }
        } else if (!onSubmit) {
          failureDetail = NO_DELIVERY_PATH;
          throw new Error('LeadCapture has no submitEndpoint or onSubmit');
        }
        setFormState('success');
        setEmail('');
        setName('');
        setWebsite('');
        setConsent(false);
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console -- dev-only diagnostic; production reads the error-state data attributes
          console.error(`[LeadCapture] Submission failed (${failureDetail}).`, err);
        }
        setSubmitFailure(failureDetail);
        setFormState('error');
      }
    },
    [email, name, website, consent, onSubmit, submitEndpoint, submitFormat, showName, showWebsite, showConsent, validateEmail]
  );

  const rootClass = `lead-capture lead-capture--${variant}`;
  const isSuccess = formState === 'success';
  const isError = formState === 'error';
  const isConfigError = submitFailure === NO_DELIVERY_PATH;

  // Shared header column (heading + optional eyebrow/badge/subheading/list),
  // reused by the idle form and the success confirmation so the heading keeps
  // its position and 514px width in both states.
  const headerContent = (
    <div className="lead-capture__content">
      {(badgeText || eyebrow) ? (
        <div className="lead-capture__eyebrow-row">
          {badgeText ? (
            <Badge size="sm" tone="dark" icon={badgeIcon} iconUrl={badgeIconUrl}>
              {badgeText}
            </Badge>
          ) : null}
          {eyebrow ? <span className="lead-capture__eyebrow">{eyebrow}</span> : null}
        </div>
      ) : null}
      <h2 id={`${fieldId}-heading`} className="lead-capture__heading">
        {heading}
      </h2>
      {subheading ? (
        <RichText content={subheading} className="lead-capture__subheading" />
      ) : null}
      {listMarkdown ? (
        <RichText
          content={listMarkdown}
          className="lead-capture__list"
        />
      ) : null}
    </div>
  );

  // Success — header stays on the left (514px, unchanged); the green banner
  // fills the form + CTA slot to the right edge. Auto-dismisses via the effect.
  if (isSuccess) {
    return (
      <section className={rootClass} aria-labelledby={`${fieldId}-heading`} {...blockAnchorAttributes(anchorId)}>
        <div className="lead-capture__inner">
          {headerContent}
          <div
            className="lead-capture__notification lead-capture__notification--success lead-capture__notification--full"
            role="status"
          >
            <span className="lead-capture__notification-icon" aria-hidden="true">
              <CustomIcon name="check" size={24} />
            </span>
            <span className="lead-capture__notification-title">{successHeading}</span>
          </div>
          {successCtaText && hasCtaDestination(successCtaHref, successCtaAction, successCtaTargetPopupId) ? (
            <CtaButton
              href={successCtaHref}
              action={successCtaAction}
              popupTargetId={successCtaTargetPopupId}
              colorVariant="secondary"
              size="medium"
            >
              {successCtaText}
            </CtaButton>
          ) : null}
        </div>
      </section>
    );
  }

  // Error — heading (left) · notification pill (middle) · optional CTA (right).
  // Persists until the user retries (no auto-dismiss).
  if (isError) {
    return (
      <section
        className={rootClass}
        aria-labelledby={`${fieldId}-heading`}
        data-poly-error={isConfigError ? 'lead-capture-missing-endpoint' : undefined}
        data-poly-error-detail={isConfigError ? submitFailure : undefined}
        data-poly-submit-failure={submitFailure && !isConfigError ? submitFailure : undefined}
        {...blockAnchorAttributes(anchorId)}
      >
        <div className="lead-capture__status-block">
          <div className="lead-capture__status-row">
            <h2 id={`${fieldId}-heading`} className="lead-capture__status-heading">
              {heading}
            </h2>
            <div
              className="lead-capture__notification lead-capture__notification--error"
              role="alert"
            >
              <span className="lead-capture__notification-icon" aria-hidden="true">
                <CustomIcon name="alert-circle" size={24} />
              </span>
              <span className="lead-capture__notification-title">{errorHeading}</span>
            </div>
            {errorCtaText && hasCtaDestination(errorCtaHref, errorCtaAction, errorCtaTargetPopupId) ? (
              <CtaButton
                href={errorCtaHref}
                action={errorCtaAction}
                popupTargetId={errorCtaTargetPopupId}
                colorVariant="secondary"
                size="medium"
              >
                {errorCtaText}
              </CtaButton>
            ) : null}
            <button
              type="button"
              className="lead-capture__retry"
              onClick={() => setFormState('idle')}
            >
              Try again
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={rootClass} aria-labelledby={`${fieldId}-heading`} {...blockAnchorAttributes(anchorId)}>
      <div className="lead-capture__inner">
        {headerContent}
        <form
          className="lead-capture__form"
          onSubmit={handleSubmit}
          noValidate
        >
          <div className="lead-capture__fields">
            {showName ? (
              <div className="lead-capture__field">
                <label
                  htmlFor={nameId}
                  className="lead-capture__label lead-capture__label--sr-only"
                >
                  {nameLabel}
                </label>
                <input
                  id={nameId}
                  type="text"
                  className="lead-capture__input"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder={namePlaceholder ?? nameLabel}
                  autoComplete="name"
                  disabled={formState === 'submitting'}
                />
              </div>
            ) : null}
            {showWebsite ? (
              <div className="lead-capture__field">
                <label
                  htmlFor={websiteId}
                  className="lead-capture__label lead-capture__label--sr-only"
                >
                  {websiteLabel}
                </label>
                {/* type="url" would reject "mybusiness.com" without a scheme, so a text input with a URL keyboard is used. */}
                <input
                  id={websiteId}
                  type="text"
                  inputMode="url"
                  className="lead-capture__input"
                  value={website}
                  onChange={(event) => setWebsite(event.target.value)}
                  placeholder={websitePlaceholder ?? websiteLabel}
                  autoComplete="url"
                  disabled={formState === 'submitting'}
                />
              </div>
            ) : null}
            <div className="lead-capture__field">
              <label
                htmlFor={emailId}
                className="lead-capture__label lead-capture__label--sr-only"
              >
                {emailLabel}
              </label>
              <input
                id={emailId}
                type="email"
                className={`lead-capture__input${
                  emailError ? ' lead-capture__input--error' : ''
                }`}
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  if (emailError) setEmailError(null);
                }}
                placeholder={emailPlaceholder ?? emailLabel}
                autoComplete="email"
                required
                aria-required="true"
                aria-invalid={emailError ? 'true' : 'false'}
                aria-describedby={emailError ? emailErrorId : undefined}
                disabled={formState === 'submitting'}
              />
              {emailError ? (
                <div id={emailErrorId} className="lead-capture__error" role="alert">
                  {emailError}
                </div>
              ) : null}
            </div>
          </div>
          {showConsent ? (
            <div className="lead-capture__consent">
              <input
                id={consentId}
                type="checkbox"
                className="lead-capture__consent-checkbox"
                checked={consent}
                onChange={(event) => setConsent(event.target.checked)}
                disabled={formState === 'submitting'}
                aria-label={consentLabel || undefined}
                aria-labelledby={!consentLabel && consentText ? consentTextId : undefined}
              />
              {consentText ? (
                <div id={consentTextId} className="lead-capture__consent-text">
                  <RichText content={consentText} />
                </div>
              ) : null}
            </div>
          ) : null}
          <div className="lead-capture__cta-group">
            <div className="lead-capture__actions">
              <CtaButton
                formAction="submit"
                colorVariant={submitCtaColorVariant}
                size="medium"
                disabled={formState === 'submitting'}
              >
                {formState === 'submitting' ? 'Submitting…' : submitCtaText}
              </CtaButton>
            </div>
            {incentivesText ? (
              <RichText
                content={incentivesText}
                className="lead-capture__incentives"
              />
            ) : null}
          </div>
        </form>
      </div>
    </section>
  );
};
